import { isEqual } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import eventBus from '../utils/events'
import { ROUTES } from '@router/modules/constants'
import { VALIDATION_MESSAGES } from '@/constants/validationMessages'

const prodDepRoles = ['business_analyst', 'designer', 'project_manager', 'qa', 'developer']

export default {
  data () {
    return {
      isLogin: !!localStorage.getItem('access_token'),
      isDataPageLoading: false,
      standalonePages: [
        ROUTES.SIGN_IN.name,
        ROUTES.SIGN_UP.name,
        ROUTES.PASSWORD_RESET.name,
        ROUTES.PASSWORD_UPDATE.name,
        ROUTES.CONFIRM_EMAIL.name,
        ROUTES.COMPLETE_SIGN_UP.name,
        ROUTES.ADD_AGENCY.name,
        ROUTES.ADD_AGENCY_ATLASSIAN.name,
        ROUTES.INVITE_TEAMMATES.name,
        ROUTES.INVITE_TEAMMATES_ATLASSIAN.name,
        ROUTES.CONFIRMATION_LINK_EXPIRED.name,
        ROUTES.INVITATION_LINK_EXPIRED.name,
        ROUTES.WELCOME.name,
        ROUTES.AVAILABLE_PROFILES.name
      ]
    }
  },
  computed: {
    ...mapGetters({
      isJiraAuth: 'isJiraAuth',
      getUser: 'getUser'
    }),
    ROUTES () {
      return ROUTES
    },
    VALIDATION_MESSAGES () {
      return VALIDATION_MESSAGES
    },
    appDomain () {
      return process.env.VUE_APP_DOMAIN
    },
    isTestingE2E () {
      const urlParams = new URLSearchParams(window.location.search)
      const isTestingE2E = urlParams.has('isTestingE2E')

      return process.env.VUE_APP_ENV === 'development' && isTestingE2E
    },
    isTenantDomain () {
      /**
       * VUE_APP_CENTRAL_DOMAINS represents coma-separated list of domains where the app can run which are NOT a tenant-specific domains
       * Here are examples of tenant domains (for production): intel.easyagency.io, mycompany.easyagency.io, exampletenant.easyagency.io
       * Central domains are (for production): easyagency.io, app.easyagency.io
       */

      return !process.env.VUE_APP_CENTRAL_DOMAINS.split(',').includes(window.location.host)
    },
    _allUserRoles: {
      get: function () {
        return this.$store.getters.getRoles
      }
    },
    _prodDepUserRoles: {
      get: function () {
        return this._allUserRoles.filter(role => {
          return prodDepRoles.includes(role.name)
        })
      }
    },
    _UserInfo: {
      get: function () {
        return this.$store.getters.getUser
      }
    },
    _prodDepUsersWithTrashed () {
      return this.$store.getters.getAllUsersWithTrashed.filter(user => {
        return user.roles.some(role =>
          prodDepRoles.includes(role.name)
        )
      })
    },
    _isAdmin () {
      return (
        this.$store.getters.getCurrentUserRole.indexOf('administrator') > -1
      )
    },
    _isUnitManager () {
      return this.$store.getters.getCurrentUserRole.indexOf('UnitManager') > -1
    },
    _isProjectManager () {
      return (
        this.$store.getters.getCurrentUserRole.indexOf('ProjectManager') > -1
      )
    },
    _isQA () {
      return this.$store.getters.getCurrentUserRole.indexOf('QA') > -1
    },
    _isAnother () {
      return this.$store.getters.getCurrentUserRole.indexOf('Another') > -1
    }
  },
  methods: {
    ...mapActions({
      setIsJiraAuth: 'setIsJiraAuth'
    }),
    _tenantName () {
      return 'Giraffe Software'
    },
    _getPartnerRelatedUsers (partnerId) {
      return this.$store.getters.getAllUsers.filter(user => {
        return user.partner_id === partnerId
      })
    },
    _removeProperty (prop) {
      return ({ [prop]: _, ...rest }) => rest
    },
    $can (permissionName) {
      let Permission = this.$store.getters.getPermission || ''
      return Permission.indexOf(permissionName) !== -1
    },
    fromDateToUTC (date, format = 'YYYY-MM-DD') {
      return this.$moment.utc(date, format)
    },
    _toIsoStringTime (time) {
      return this.$moment(time).toISOString()
    },
    _toLocalTime (time, format = 'YYYY-MM-DD', def = null) {
      if (this.$moment(time).isValid()) {
        return this.$moment(time).format(format)
      }
      return def
    },
    _localNowTime (format = 'YYYY-MM-DD') {
      return this.$moment().format(format)
    },
    async _toggleDataPageLoading (func) {
      this.isDataPageLoading = true
      return func().catch(error => {
        let errors = []
        if (error.response?.data?.errors) {
          errors = Object.values(error.response.data.errors)
        } else if (error.response?.data?.error) {
          errors = [[error.response.data.error]]
        }
        for (var i = 0; i < errors.length; i++) {
          this.$snotify.error(
            errors[i][0],
            'Error',
            this.$store.state.snotify
          )
        }
      }).finally(() => {
        this.isDataPageLoading = false
      })
    },
    _setRouteByFields (fields) {
      const setQuery = {}

      for (let item in fields) {
        if (Array.isArray(fields[item].value)) {
          fields[item].value.forEach((element, index) => {
            setQuery[item + index] = element.id.toString()
          })
          continue
        }

        if (Object.prototype.toString.call(fields[item].value) === '[object Date]') {
          setQuery[item] = this._toLocalTime(fields[item].value)
          continue
        }

        if (fields[item].value) {
          setQuery[item] = fields[item].value.toString()
        }
      }

      const isSameParams = isEqual(this.$route.query, setQuery)

      if (isSameParams) return

      this.$router.replace({ query: setQuery })
    },
    errorNotifications (error) {
      if (error?.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors)

        if (!errors.length) {
          this.$snotify.error(
            error.response.data?.message || 'Error',
            'Error',
            this.$store.state.snotify
          )
          return
        }
        if (errors.length === 1) {
          const errorText = errors[0][0]
          const errorHtml = `
                            <div class="snotifyToast__title">Error</div>
                            <div class="snotifyToast__body">${errorText}</div>
                            <div class="snotify-icon snotify-icon--error"></div>`
          this.$snotify.error(
            'Error',
            { ...this.$store.state.snotify, html: errorHtml }
          )
        } else {
          let errorText = ''
          for (var i = 0; i < errors.length; i++) {
            errorText += errors[i][0] + '<br/>'
          }
          const errorHtml = `
                            <div class="snotifyToast__title">Error</div>
                            <div class="snotifyToast__body">${errorText}</div>
                            <div class="snotify-icon snotify-icon--error"></div>`
          this.$snotify.error(
            'Error',
            { ...this.$store.state.snotify, html: errorHtml }
          )
        }
        return
      }

      this.$snotify.error(
        error?.response?.data?.message || 'Error',
        'Error',
        this.$store.state.snotify
      )
    },
    successNotifications (message, htmlData = null) {
      if (htmlData) {
        const successHtml = `
                            <div class="snotifyToast__title">Success</div>
                            <div class="snotifyToast__body">${htmlData}</div>
                            <div class="snotify-icon snotify-icon--success"></div>`
        this.$snotify.success(
          'Success',
          { ...this.$store.state.snotify, html: successHtml }
        )
      } else {
        this.$snotify.success(
          message,
          'Success',
          this.$store.state.snotify
        )
      }
    },
    fileToBase64 (file) {
      return new Promise(function (resolve, reject) {
        let reader = new FileReader()
        reader.onload = function (event) {
          let base64String = event.target.result
          resolve(base64String)
        }
        reader.onerror = function (error) {
          reject(error)
        }
        reader.readAsDataURL(file)
      })
    },
    _checkIsJiraAuth () {
      if (!this.isJiraAuth) {
        this.$snotify.error(
          'You need Jira Auth for these action',
          'Error',
          this.$store.state.snotify
        )
        return false
      }
      return true
    },
    _hexToRGB (hex, alpha) {
      if (typeof hex !== 'string' || hex.length < 7) return hex

      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)

      if (alpha) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      } else {
        return `rgb(${r}, ${g}, ${b})`
      }
    },
    logInToJira () {
      this.axios({
        method: 'get',
        url: '/api/generate-jira-auth-url',
        headers: {
          Authorization: 'Bearer' + localStorage.getItem('access_token')
        }
      })
        .then((response) => {
          const jiraAuthWindow = window.open(
            response.data.url,
            'Sign in with Atlassian',
            'popup'
          )

          if (!jiraAuthWindow) {
            this.$snotify.error('Popup blocked. Please allow popups for this site.', 'Error', this.$store.state.snotify)
            return
          }

          const messageListener = async (event) => {
            const eventData = event.data
            const popupOrigin = event.origin
            const windowOrigin = window.location.origin

            // Only process messages from our domain with valid event_id
            if (windowOrigin === popupOrigin && eventData && eventData.event_id) {
              if (eventData.event_id === 'jira-auth-success') {
                await this.fetchIsJiraAuth()
                this.$snotify.success(
                  'You are successfully logged in Jira ',
                  'Success',
                  this.$store.state.snotify
                )
              } else if (eventData.event_id === 'auth-failed') {
                this.$snotify.info(
                  'Your Atlassian auth is failed',
                  'Error',
                  this.$store.state.snotify
                )
              } else if (eventData.event_id === 'no-jira-credentials') {
                this.$snotify.info(
                  'Please specify Jira credentials',
                  'Error',
                  this.$store.state.snotify
                )
              } else if (eventData.event_id === 'auth-in-app') {
                await this.authUserAtFrontEnd(eventData.auth_data.access_token, eventData.auth_data.refresh_token, eventData.auth_data.user, eventData.auth_data.roles)
              }

              if (['jira-auth-success', 'auth-failed', 'no-jira-credentials', 'auth-in-app'].includes(eventData.event_id)) {
                window.removeEventListener('message', messageListener)
                if (!jiraAuthWindow.closed) {
                  jiraAuthWindow.close()
                }
              }
              // Don't close for other event_id values - they might be spurious
            }
          }

          window.addEventListener('message', messageListener)

          // Clean up if popup is closed manually
          const checkClosed = setInterval(() => {
            if (jiraAuthWindow.closed) {
              window.removeEventListener('message', messageListener)
              clearInterval(checkClosed)
            }
          }, 1000)
        })
        .catch((err) => {
          if (err.response?.data?.errors) {
            this.errorNotifications(err)
          }
        })
    },
    async fetchIsJiraAuth () {
      const isJiraAuthenticatedResponse = await this.axios({
        method: 'get',
        url: '/api/is-jira-auth',
        headers: {
          Authorization: 'Bearer' + localStorage.getItem('access_token')
        }
      })
      this.setIsJiraAuth(!!isJiraAuthenticatedResponse.data.is_auth)
    },
    async authUserAtFrontEnd (accessToken, refreshToken, user, roles) {
      await this.axios
        .get(`/api/permissions/${user.id}`)
        .then(({ data }) => {
          this.$store.dispatch('setAccessToken', accessToken)
          localStorage.setItem('refresh_token', refreshToken)
          this.$store.dispatch('setUser', user)
          this.$store.dispatch('setUserRoles', roles)
          this.$store.dispatch('setPermission', data)
          this.$store.dispatch('setUserName', user.name)
          this.$store.dispatch('setUserId', user.id)
          this.$store.dispatch('setActive', false)

          const redirectUrlPath = localStorage.getItem('redirect_url_path')
          if (this.$route.name === this.ROUTES.SIGN_IN.name) {
            if (redirectUrlPath && redirectUrlPath !== this.ROUTES.SIGN_IN.path) {
              localStorage.removeItem('redirect_url_path')
              this.$router.push(redirectUrlPath)
            } else {
              this.$router.push({ name: this.ROUTES.HOME.name })
            }
          }
          this.fetchIsJiraAuth()
          eventBus.$emit('login', {})
        })
        .catch(error => {
          this.$snotify.error(
            error.response?.data?.error,
            'Error',
            this.$store.state.snotify
          )
        })
    }
  }
}
